import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, X } from 'lucide-react'
import { groupRosterQueryOptions } from '@/utils/rosterQueryOptions'
import { hasOverlays } from '@/types/rosterTypes'
import { RosterList } from './RosterList'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

/**
 * The roster behind one group chip, opened from the recipient field.
 *
 * Adding "SMS.ED-Clerk-GMCH" to a message is otherwise an act of faith: the
 * chip says a name and a number falls out at the bottom of the screen. This is
 * the same list the Groups tab shows, at the moment it matters most — before
 * anyone hits send.
 */
export function GroupRosterPanel({
  groupId,
  onClose,
}: {
  groupId: string
  onClose: () => void
}) {
  const { data: roster, isPending, error } = useQuery(groupRosterQueryOptions(groupId))
  const [onlyMissing, setOnlyMissing] = useState(false)

  return (
    <div className="mt-2 overflow-hidden rounded-xl border-[1.5px] border-border bg-surface shadow-wha">
      <div className="flex items-start gap-2 border-b border-border-2 bg-surface-2 px-[13px] py-2.5">
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-bold text-foreground">
            {roster?.displayName ?? 'Group members'}
          </div>
          {roster && (
            <div className="mt-px text-[11.5px] text-muted-foreground">
              <span className="font-bold text-foreground">{roster.counts.textable}</span> of{' '}
              {roster.counts.effective} get the text
              {hasOverlays(roster) && (
                <>
                  {roster.counts.added > 0 && <> · {roster.counts.added} added</>}
                  {roster.counts.excluded > 0 && <> · {roster.counts.excluded} excluded</>}
                </>
              )}
            </div>
          )}
        </div>

        {roster && roster.counts.unreachable > 0 && (
          <button
            type="button"
            onClick={() => setOnlyMissing((v) => !v)}
            className={cn(
              'inline-flex flex-none items-center gap-1 rounded-[7px] border px-2 py-1 text-[11px] font-bold transition-colors',
              onlyMissing
                ? 'border-warn-border bg-warn-tint text-warn-ink'
                : 'border-border bg-surface text-warn-ink hover:bg-warn-tint',
            )}
          >
            <AlertTriangle className="h-[11px] w-[11px]" />
            {roster.counts.unreachable} no mobile
          </button>
        )}

        <button
          type="button"
          onClick={onClose}
          className="flex-none p-0.5 text-muted-foreground hover:text-foreground"
          aria-label="Close group members"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="max-h-[240px] overflow-y-auto">
        {isPending ? (
          <div className="space-y-2 p-2.5">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-9 w-full rounded-[9px]" />
            ))}
          </div>
        ) : error || !roster ? (
          <div className="px-4 py-5 text-center text-[12.5px] text-destructive">
            Couldn't load this group's members.
          </div>
        ) : (
          <RosterList
            members={
              onlyMissing ? roster.members.filter((m) => !m.hasMobile) : roster.members
            }
            showSource={hasOverlays(roster)}
            emptyMessage="This group has no members — it would reach nobody."
          />
        )}
      </div>
    </div>
  )
}
